import React, { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import Countdown from 'react-countdown-now';
import moment from 'moment';
import get from 'lodash/get';
import has from 'lodash/has';
import { AudioOutlined } from '@ant-design/icons';
import { Button } from 'antd';
//
import { createRecorder } from '../../utils/audio-workaround/create-recorder';
import { createObjectURL, revokeObjectURL } from '../../utils/window';
import { showErrorNotification } from 'utils/error-handlers';
import { AudioPlaybackControls } from './AudioPlaybackControls';
import styles from './AudioRecorder.less';

const MAX_LENGTH_SECONDS = 5 * 60;

export const AudioRecorder = (props) => {
  const {
    label,
    value,
    width,
    height,
    disabled,
    onStop,
    onStart,
    disableButton,
    showPlaybackControls,
  } = props;

  const [isRecording, setIsRecording] = useState(false);
  const [blobURL, setBlobURL] = useState(value);
  const [count, setCount] = useState(0);
  const [error, setError] = useState();

  const recordAud = useRef();
  const countTimer = useRef();
  const canvas = useRef();

  useEffect(() => {
    return () => {
      clearInterval(countTimer.current);
      if (has(recordAud, 'current.stop')) {
        recordAud.current.stop();
      }
    };
  }, []);

  const stopRecording = useCallback(() => {
    clearInterval(countTimer.current);

    try {
      const audio = recordAud.current.stop();
      setCount(0);
      if (audio.audioBlob.size < 500) {
        throw new Error('Audio recording too large. Please try again and shorten your recording.');
      }

      setBlobURL(createObjectURL(audio.audioBlob));
      onStop(audio.audioBlob);
    } catch (e) {
      showErrorNotification(e.message);
    } finally {
      setIsRecording(false);
    }
  }, [onStop]);

  useEffect(() => {
    if (count > MAX_LENGTH_SECONDS) {
      stopRecording();
    }
  }, [count, stopRecording]);

  useLayoutEffect(() => {
    return () => {
      const disconnect = get(recordAud.current, 'disconnect');
      if (disconnect) disconnect();
    };
  }, []);

  const handleRecord = async () => {
    if (error) return;

    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  };

  const startRecording = async () => {
    if (blobURL) revokeObjectURL(blobURL);

    try {
      recordAud.current = await createRecorder(canvas.current, width, height);
    } catch (e) {
      const errMsg =
        'MeeOpp was not able to detect a microphone on your device. Please check your settings and refresh the page.';

      setError(errMsg);
      return;
    }

    recordAud.current.start();
    countTimer.current = setInterval(() => {
      setCount((prevCount) => prevCount + 1);
    }, 1000);

    setIsRecording(true);
    onStart();
  };

  // eslint-disable-next-line react/prop-types
  const renderCount = ({ minutes, seconds }) => {
    return <span>{moment().minute(minutes).second(seconds).format('mm : ss')}</span>;
  };

  return (
    <div className={styles.audioRecorderContainer}>
      {error && <p className="response-label has-text-danger">{error}</p>}
      {!error && !disabled && <p>{label}</p>}
      <div style={{ display: disabled || !isRecording ? 'none' : 'inherit' }}>
        <canvas className={styles.oscilloscope} width={width} height={height} ref={canvas} />
      </div>
      {!error && !disabled && (
        <>
          <Button
            type="danger"
            shape="circle"
            icon={<AudioOutlined />}
            size="large"
            onClick={handleRecord}
            disabled={disableButton}
          />
          {isRecording && (
            <div className={styles.elapsedTime}>
              <Countdown date={moment().add(count, 'seconds').format()} renderer={renderCount} />
            </div>
          )}
        </>
      )}
      {showPlaybackControls && blobURL && <AudioPlaybackControls url={blobURL} />}
    </div>
  );
};

AudioRecorder.propTypes = {
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabled: PropTypes.bool,
  onStart: PropTypes.func,
  onStop: PropTypes.func,
  disableButton: PropTypes.bool,
  showPlaybackControls: PropTypes.bool,
};

AudioRecorder.defaultProps = {
  label: 'Record Audio',
  width: 640,
  height: 100,
  disabled: false,
  value: null,
  disableButton: false,
  showPlaybackControls: true,
  onStart: () => null,
  onStop: () => null,
};
